/**
 * تطبيق التشغيل is its own app (owner, 2026-10-08: "لوحده يفتحو بيوزر بتاعه مش يوصلها من صفحه العمليات" ·
 * "ونثبته pwa"): its own address (/ops), its own sign-in (/ops/login), its own installable manifest
 * (public/ops.webmanifest), and the screens it opens stay inside it (/ops/<page path>) — no sidebar, no main menu.
 */
import { useLocation } from 'react-router-dom'
import { latinDigits, mineAwbs } from '@/domain/awb'

export const OPS_HOME = '/ops'
export const OPS_LOGIN = '/ops/login'

/** The screens the floor opens from the app, by their path in the main app. */
export const OPS_PAGES = [
  { path: '/operations/shipments/new', label: 'إنشاء شحنة' },
  { path: '/operations/shipments/upload', label: 'رفع ملف Excel' },
  { path: '/operations/drs/new', label: 'توزيع على مندوب' },
  { path: '/operations/runner-returns', label: 'مرتجعات المناديب' },
  { path: '/operations/return-to-shipper/new', label: 'مرتجع جديد' },
  { path: '/operations/shipments/bulk-clone', label: 'شحنات مماثلة' },
  { path: '/operations/weights', label: 'الأوزان' },
  { path: '/operations/not-received', label: 'لم يتم الاستلام' },
  { path: '/reports/awb-tracking', label: 'تتبع شحنة' },
  { path: '/reports/shipment-details', label: 'بحث بالجملة' },
] as const

export const opsPath = (path: string) => `${OPS_HOME}${path}`
export const isOpsPath = (pathname: string) => pathname === OPS_HOME || pathname.startsWith(`${OPS_HOME}/`)

/**
 * A link from a screen that is also one of the app's screens stays inside the app when the screen is open there
 * (the bulk search's result cards open the shipment inside the app, not in the main one).
 */
export function useOpsHref() {
  const inOps = isOpsPath(useLocation().pathname)
  return (to: string) => (inOps && OPS_PAGES.some((p) => to === p.path || to.startsWith(`${p.path}?`)) ? opsPath(to) : to)
}

/**
 * The codes in whatever was pasted or typed — "ALS123 حالته اي", a WhatsApp message, rows out of a sheet: only the
 * codes (and mobiles) are searched, the words are left out and shown (owner, 2026-10-08).
 */
export const findCodes = (text: string) => mineAwbs(latinDigits(text))

/** Where a search goes: one code opens the shipment, more (or a mobile) the bulk search. */
export function searchTarget(text: string) {
  const { awbs, mobiles } = findCodes(text)
  if (awbs.length === 1 && !mobiles.length) return `${opsPath('/reports/awb-tracking')}?awb=${encodeURIComponent(awbs[0]!)}`
  if (!awbs.length && !mobiles.length) return null
  const q = new URLSearchParams()
  if (awbs.length) q.set('awbs', awbs.join(','))
  if (mobiles.length) q.set('mobiles', mobiles.join(','))
  return `${opsPath('/reports/shipment-details')}?${q.toString()}`
}

/** The title on the app's bar for where it is now. */
export function opsTitle(pathname: string) {
  return OPS_PAGES.find((p) => pathname.startsWith(opsPath(p.path)))?.label ?? 'التشغيل'
}

/**
 * The page came out of an older service worker's main shell (manifest /manifest.webmanifest, start /), so "Add to
 * Home Screen" would install the whole system (owner, 2026-10-09: "بردو ال pwa مش ظابط بيفتح السيستم كله"). Fetch
 * the new worker, let it take over, and load the page once more from the server's ops.html. Once per tab.
 */
export async function healOpsShell(doc: Document = document, win: Window = window): Promise<boolean> {
  const href = doc.querySelector('link[rel="manifest"]')?.getAttribute('href') ?? ''
  if (href.includes('ops.webmanifest')) return false
  const key = 'aos-ops-heal'
  try { if (win.sessionStorage.getItem(key)) return false; win.sessionStorage.setItem(key, '1') } catch { return false }
  const sw = win.navigator.serviceWorker
  if (sw) {
    const reg = await sw.getRegistration().catch(() => undefined)
    await Promise.race([
      new Promise<void>((done) => sw.addEventListener('controllerchange', () => done(), { once: true })),
      reg ? reg.update().then(() => new Promise<void>((done) => setTimeout(done, 1500))) : Promise.resolve(),
      new Promise<void>((done) => setTimeout(done, 4000)),
    ]).catch(() => undefined)
  }
  win.location.reload()
  return true
}

/**
 * Points the page at the ops app's manifest, so "Add to Home screen" / "Install" from /ops installs «التشغيل»
 * (start /ops) and not the whole Aliens OS. Run at start-up, before the browser reads the manifest.
 */
export function applyOpsManifest(doc: Document = document) {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!link) { link = doc.createElement('link'); link.rel = 'manifest'; doc.head.appendChild(link) }
  link.href = '/ops.webmanifest'
  let title = doc.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
  if (!title) { title = doc.createElement('meta'); title.name = 'apple-mobile-web-app-title'; doc.head.appendChild(title) }
  title.content = 'التشغيل'
  doc.title = 'التشغيل · COREX'
}
